// DICE.EXE — roll under your number.
//
// The target is a number in hundredths, exactly as the contract holds it: 5000 is "roll under
// 50.00". Slide it up for a safer roll and a smaller multiple, down for a bigger one. The door
// accepts DICE_MIN_TARGET..DICE_MAX_TARGET and the slider is built from those two constants, so
// it can never offer a ticket the cabinet would refuse.
//
// The odometer scrambles while the round is in the air — that scramble is decoration and nothing
// is decided by it. What it settles on is rollOf() read back from the cabinet for that round.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealDice } from 'lib/casino';
import { DICE_MAX_TARGET, DICE_MIN_TARGET, diceChanceWad, dicePayoutMultBps } from 'lib/casino-math';
import sfx from 'lib/sfx';
import { AutoPanel, useAuto } from './auto';
import { useBet, useStakeCap } from './state';
import { FONT, HouseRules, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, fmtPctFromWad, parseUnits, xpButton } from './ui';

const BPS = BigInt(10000);
const ALL_IN_BPS = BigInt(10500);
const ROLL_SPACE = 10000; // hundredths: 0..9999
const SCRAMBLE_MS = 70;
const DEFAULT_TARGET = 5000;
const QUICK_TARGETS = [500, 2500, 5000, 7500, 8900].filter(t => t >= DICE_MIN_TARGET && t <= DICE_MAX_TARGET);

const hundredths = v => (v / 100).toFixed(2);

/** The four digit tiles of a roll, plus the point between them. */
function Odometer({ value, state }) {
  const text = hundredths(value).padStart(5, '0');
  return (
    <div className={`dc__odo dc__odo--${state}`}>
      {text.split('').map((ch, i) =>
        ch === '.' ? (
          <i key={i} className="dc__point" />
        ) : (
          <span key={i} className="dc__digit">
            {ch}
          </span>
        ),
      )}
      <span className="dc__pct">%</span>
    </div>
  );
}

export default function Dice({ casino }) {
  const [amount, setAmount] = useState('5');
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [roll, setRoll] = useState(null);
  const [scramble, setScramble] = useState(DEFAULT_TARGET);
  const bet = useBet('dice', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';
  const pending = bet.phase === 'approving' || bet.phase === 'placing' || bet.phase === 'waiting' || bet.phase === 'settling';

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BPS) / ALL_IN_BPS;
  const multBps = dicePayoutMultBps(target);
  const payout = (stake * multBps) / BPS;
  const chanceWad = diceChanceWad(target);

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('dice', Number(multBps), casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && !overCap;
  const auto = useAuto(bet, { allIn, args: [target], canPlay, balance: casino.balance });

  // The settled roll, read back from the cabinet's own view of the round.
  useEffect(() => {
    let alive = true;
    setRoll(null);
    if (!bet.result) return undefined;
    revealDice(bet.result.id, bet.result.randomness)
      .then(r => {
        if (alive) setRoll(Number(r));
      })
      .catch(() => {
        // The receipt below is the authority either way; showing no number beats showing a wrong one.
        if (alive) setRoll(null);
      });
    return () => {
      alive = false;
    };
  }, [bet.result]);

  // The scramble: display only, bounded by the pending phase and torn down with it.
  const tickRef = useRef(null);
  useEffect(() => {
    if (!pending) return undefined;
    if (sfx.diceRoll) sfx.diceRoll();
    tickRef.current = setInterval(() => setScramble(Math.floor(Math.random() * ROLL_SPACE)), SCRAMBLE_MS);
    return () => {
      if (tickRef.current !== null) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [pending]);

  const shown = roll === null ? scramble : roll;
  const won = roll === null ? null : roll < target;
  const state = pending ? 'spin' : won === null ? 'idle' : won ? 'win' : 'loss';
  const zonePct = (target / ROLL_SPACE) * 100;
  const markPct = roll === null ? null : (roll / ROLL_SPACE) * 100;

  return (
    <Div>
      <div className="dc__table">
        <div className="dc__cap">ROLL UNDER</div>
        <div className="dc__target">{hundredths(target)}</div>
        <Odometer value={shown} state={state} />
        <div className="dc__verdict">
          {roll === null
            ? pending
              ? 'the dice are in the air'
              : 'set your number and roll'
            : won
              ? `${hundredths(roll)} is under ${hundredths(target)}`
              : `${hundredths(roll)} is not under ${hundredths(target)}`}
        </div>

        <div className="dc__bar" aria-hidden="true">
          <div className="dc__zone" style={{ width: `${zonePct}%` }} />
          <div className="dc__zoneLabel" style={{ width: `${zonePct}%` }}>
            WIN
          </div>
          {markPct === null ? null : (
            <div className={`dc__mark${won ? ' win' : ''}`} style={{ left: `${markPct}%` }} />
          )}
        </div>
        <div className="dc__scale">
          <span>0.00</span>
          <span>50.00</span>
          <span>99.99</span>
        </div>
      </div>

      <Panel>
        <div className="dc__slider">
          <label htmlFor="dc-target">Target</label>
          <input
            id="dc-target"
            type="range"
            min={DICE_MIN_TARGET}
            max={DICE_MAX_TARGET}
            step="1"
            value={target}
            disabled={locked}
            onChange={e => setTarget(Number(e.target.value))}
          />
          <span className="dc__value">{hundredths(target)}</span>
        </div>
        <div className="dc__quick">
          {QUICK_TARGETS.map(t => (
            <button key={t} type="button" className={`dc__pick${t === target ? ' on' : ''}`} disabled={locked} onClick={() => setTarget(t)}>
              {hundredths(t)}
            </button>
          ))}
        </div>

        <div className="dc__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">{(Number(multBps) / 10000).toFixed(4)}x</span>
          </div>
          <div>
            <span className="k">Chance</span>
            <span className="v">{fmtPctFromWad(chanceWad)}</span>
          </div>
          <div>
            <span className="k">Returns</span>
            <span className="v">
              {fmtChip(payout)} {CHIP.symbol}
            </span>
          </div>
        </div>

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="ROLL"
          onClick={() => bet.play(allIn, [target])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        <AutoPanel auto={auto} practice={casino.practice} />

        {bet.phase === 'stuck' ? (
          <div className="dc__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset}>
          {roll === null ? null : (
            <div className="dc__receipt">
              rolled <b>{hundredths(roll)}</b> against a target of {hundredths(target)}
            </div>
          )}
        </Outcome>

        <HouseRules
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .dc__table {
    background: radial-gradient(ellipse at 50% 26%, #1a8a4c, ${T.felt} 55%, ${T.feltDark} 100%);
    border: 4px solid #5a3a12;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px ${T.goldDeep}, inset 0 0 40px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.5);
    padding: 14px 16px 16px;
    text-align: center;
  }
  .dc__cap {
    font: bold 10px ${FONT.serif};
    letter-spacing: 5px;
    color: ${T.gold};
  }
  .dc__target {
    font: bold 15px ${FONT.web};
    color: ${T.cream};
    letter-spacing: 2px;
    margin-top: 2px;
  }
  .dc__odo {
    display: inline-flex;
    align-items: flex-end;
    gap: 3px;
    margin-top: 9px;
    padding: 6px 8px;
    background: #000;
    border: 2px inset #808080;
    box-shadow: inset 0 0 14px rgba(255, 59, 59, 0.18);
  }
  .dc__digit {
    display: inline-block;
    min-width: 26px;
    font: bold 38px ${FONT.mono};
    line-height: 1;
    color: ${T.led};
    text-shadow: 0 0 8px currentColor;
  }
  .dc__point {
    display: block;
    width: 6px;
    height: 6px;
    margin: 0 1px 5px;
    background: ${T.led};
    box-shadow: 0 0 6px currentColor;
    color: ${T.led};
  }
  .dc__pct {
    font: bold 14px ${FONT.mono};
    color: ${T.led};
    padding-bottom: 5px;
    text-shadow: 0 0 8px currentColor;
  }
  .dc__odo--spin .dc__digit {
    opacity: 0.85;
    animation: dc-flicker 0.14s steps(2) infinite;
  }
  .dc__odo--win .dc__digit,
  .dc__odo--win .dc__pct {
    color: #4dff88;
  }
  .dc__odo--win .dc__point {
    background: #4dff88;
    color: #4dff88;
  }
  .dc__odo--win {
    box-shadow: inset 0 0 16px rgba(77, 255, 136, 0.3), 0 0 14px rgba(77, 255, 136, 0.35);
  }
  .dc__odo--loss .dc__digit,
  .dc__odo--loss .dc__pct {
    color: #ff5a5a;
  }
  @keyframes dc-flicker {
    50% {
      opacity: 0.55;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .dc__odo--spin .dc__digit {
      animation: none;
    }
  }
  .dc__verdict {
    margin-top: 8px;
    font: 10px ${FONT.web};
    color: ${T.cream};
  }
  .dc__bar {
    position: relative;
    height: 16px;
    margin-top: 11px;
    background: repeating-linear-gradient(90deg, #7a1020 0 6px, #8c1526 6px 12px);
    border: 1px solid ${T.goldDeep};
    overflow: hidden;
  }
  .dc__zone {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: repeating-linear-gradient(90deg, #0f8a48 0 6px, #12a055 6px 12px);
    border-right: 1px solid ${T.gold};
  }
  .dc__zoneLabel {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font: bold 9px ${FONT.web};
    letter-spacing: 2px;
    color: #eafff1;
    overflow: hidden;
  }
  .dc__mark {
    position: absolute;
    top: -3px;
    bottom: -3px;
    width: 3px;
    margin-left: -1px;
    background: #fff;
    box-shadow: 0 0 6px #fff;
  }
  .dc__mark.win {
    background: ${T.gold};
    box-shadow: 0 0 8px ${T.gold};
  }
  .dc__scale {
    display: flex;
    justify-content: space-between;
    margin-top: 3px;
    font: 9px ${FONT.mono};
    color: #b8d9c4;
  }

  .dc__slider {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 12px;
  }
  .dc__slider label {
    font: 11px ${FONT.ui};
    color: #000;
  }
  .dc__slider input {
    flex: 1;
    min-width: 120px;
  }
  .dc__value {
    font: bold 14px ${FONT.mono};
    color: ${T.navy};
    min-width: 56px;
    text-align: right;
  }
  .dc__quick {
    display: flex;
    gap: 5px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .dc__pick {
    ${xpButton};
    min-width: 52px;
    min-height: 21px;
    padding: 0 6px;
  }
  .dc__pick.on {
    background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 45%, #d8a72a 100%);
    border-color: #8a6a10;
    font-weight: 700;
  }

  .dc__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .dc__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .dc__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .dc__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .dc__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .dc__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
  .dc__receipt {
    font: 11px ${FONT.ui};
    margin-bottom: 4px;
  }
`;
